import { Autolinker, type AutolinkerConfig } from 'autolinker';

/** One piece of a message body: plain text, or an address to be anchored. */
export interface ReportChatBodySegment {
  /** Verbatim slice of the original body — never re-encoded or trimmed. */
  text: string;
  /** Set only on an address segment; the template binds it to `[href]`. */
  href?: string;
}

/**
 * Bare `www.` and bare-TLD matching stay off on purpose: both sides of this chat
 * write prose, where "версия 2.5" or "отчёт.pdf" would otherwise turn into dead
 * links.
 *
 * `schemeMatches` means "carries **a** scheme", not "carries http(s)": app deep
 * links are the point of this chat, `tg://` above all — on a phone it opens
 * Telegram itself instead of bouncing the student through a browser, which is
 * what report 5213 was about. Which schemes are refused is {@link BLOCKED_SCHEME}.
 */
const AUTOLINK_OPTIONS: AutolinkerConfig = {
  urls: { schemeMatches: true, wwwMatches: false, tldMatches: false },
  email: false,
  phone: false,
  mention: false,
  hashtag: false,
};

/**
 * Schemes refused an anchor. A denylist, chosen by the owner over an allowlist
 * so that a scheme nobody listed — the next messenger, the next conferencing
 * app — still clicks (SINGULAR-2869).
 *
 * **Do not describe this list in prose here. Four attempts did, and all four
 * were wrong in a way the matcher disproved.** `linkify.spec.ts` enumerates
 * what is refused and what is not, case by case, and every entry is covered by
 * a deletion mutant — that file is the specification, this comment is not.
 *
 * Two things are worth knowing before you touch it, both mutation-checked:
 *
 * - This filter only ever sees hrefs `autolinker` produced. A scheme it does not
 *   match never becomes a link at all — it stays plain text, which is the safe
 *   outcome, not a gap. Follina's real URI (`ms-msdt:/id PCWDiagnostic`) and
 *   `shell:startup` are in that group; no filter here would change them.
 * - `javascript:` and `vbscript:` are inert entries: autolinker's own validator
 *   drops them first, so deleting either from this regex breaks no test. They
 *   stay as a hedge against an autolinker upgrade dropping that validator.
 *   Angular's `SAFE_URL_PATTERN` is a second layer for `javascript:` only.
 */
/**
 * Grouped only so the reader can see which entries came in together and why the
 * neighbours of one are present. The grouping carries no behaviour — the regex
 * below is a flat alternation, and `linkify.spec.ts` is what defines the set.
 */
const BLOCKED_SCHEMES = [
  // Inert: autolinker's validator drops these before the filter runs. Kept as a
  // hedge in case an upgrade removes that validator.
  'javascript',
  'vbscript',
  // Payload and local-file carriers.
  'data',
  'blob',
  'file',
  'jar',
  // Remote mounts. Forced authentication with an NTLM leak is the reason `smb`
  // is here, and `cifs` is its alias; the rest are the same family.
  'smb',
  'cifs',
  'nfs',
  'afp',
  // Directory and WebDAV. `dav`/`webdav` matter most: Windows WebClient falls
  // back to them exactly when outbound 445 is closed, so blocking `smb` without
  // them leaves the same vector open through the window next to the door.
  'ldap',
  'ldaps',
  'dav',
  'davs',
  'webdav',
  'webdavs',
  // Desktop handlers that open an application on click.
  'ms-msdt',
  'search-ms',
  'ms-search',
  'ms-officecmd',
  'shell',
  'res',
  // Office handlers — the remote-template shape of the same class.
  'ms-word',
  'ms-excel',
  'ms-powerpoint',
  'ms-access',
  'ms-visio',
  'ms-project',
  'ms-publisher',
  'ms-infopath',
  'onenote',
  'ms-outlook',
  // Session initiation to a host taken from the message body.
  'rdp',
  'ssh',
  'sftp',
  'telnet',
  'vnc',
] as const;

/** Leading scheme of an href, if it carries one: `git+ssh://host` → `git+ssh`. */
const SCHEME_HEAD = /^([a-z0-9+.-]+):/i;

/**
 * A compound scheme is refused when ANY of its parts is blocked, so `git+ssh://`
 * and `ldap+ssl://` fall with `ssh` and `ldap` instead of sneaking past a list
 * that only ever saw the whole token. Autolinker matches these forms (its own
 * docs give `other+scheme://`), and before this check they bypassed every
 * session and forced-auth entry above — the block was on the door, the compound
 * form walked around it.
 */
function isBlockedScheme(href: string): boolean {
  const scheme = SCHEME_HEAD.exec(href)?.[1];
  return scheme === undefined
    ? false
    : scheme
        .toLowerCase()
        .split('+')
        .some(part => BLOCKED.has(part));
}

const BLOCKED = new Set<string>(BLOCKED_SCHEMES);

/**
 * Splits a message body into text and link segments so the template can render
 * addresses as anchors without ever building an HTML string.
 *
 * The body is authored by students and support alike, and its only defence has
 * always been that it reaches the DOM as text. That holds here: every segment is
 * a slice of the original string, so the template keeps interpolating text and
 * the sole difference is that some of it now sits inside an `<a>`. A markdown or
 * `innerHTML` renderer would trade that guarantee away.
 *
 * Detection is delegated to `autolinker` (already a workspace dependency, see
 * `TrmLinkifyPipe`) instead of a hand-rolled regex: the fiddly parts — dropping
 * a sentence-final period, balancing brackets around a URL — are exactly what it
 * already gets right.
 */
export function splitBodyIntoSegments(body: string): ReportChatBodySegment[] {
  const bounds = Autolinker.parse(body, AUTOLINK_OPTIONS)
    .map(match => ({
      start: match.getOffset(),
      end: match.getOffset() + match.getMatchedText().length,
      href: match.getAnchorHref(),
    }))
    // A refused scheme is not dropped from the message — it stays part of the
    // surrounding text run, which the gap logic below already covers.
    .filter(({ href }) => !isBlockedScheme(href));

  const linked = bounds.flatMap(({ start, end, href }, index) => {
    const previousEnd = index === 0 ? 0 : (bounds[index - 1]?.end ?? 0);
    const lead = start > previousEnd ? [{ text: body.slice(previousEnd, start) }] : [];
    return [...lead, { text: body.slice(start, end), href }];
  });

  // Tail after the last address — and, when nothing matched at all, the whole
  // body. An empty body still yields one empty segment so callers never have to
  // special-case a zero-length list.
  const tailStart = bounds.at(-1)?.end ?? 0;
  return tailStart < body.length || linked.length === 0 ? [...linked, { text: body.slice(tailStart) }] : linked;
}
